<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { IonToast } from '@ionic/vue';
import { storeToRefs } from 'pinia';
import { useNotificationStore, type NearbyAlertItem } from '@/stores/useNotificationStore';
import { useI18n } from '@/i18n';

const router = useRouter();
const { t } = useI18n();
const notificationStore = useNotificationStore();
const { nearbyAlerts } = storeToRefs(notificationStore);

const isOpen = ref(false);
const message = ref('');
const errorOpen = ref(false);
const errorMessage = ref('');
const claiming = ref(false);

const currentAlert = computed<NearbyAlertItem | null>(() => {
  const pending = nearbyAlerts.value.filter((a) => a.status === 'pending');
  if (!pending.length) return null;
  return pending[0];
});

function refreshMessage(alert: NearbyAlertItem | null): void {
  if (!alert) {
    message.value = '';
    return;
  }
  message.value = t('nearby_alert_subtitle', {
    user: alert.otherUsername,
    distance: alert.distanceKm.toFixed(1),
  });
}

watch(currentAlert, (alert) => {
  refreshMessage(alert);
  isOpen.value = !!alert;
}, { immediate: true });

function onNewAlertEvent(): void {
  // Force a banner re-open even if the list already contains the alert.
  if (currentAlert.value) {
    isOpen.value = false;
    requestAnimationFrame(() => { isOpen.value = true; });
  }
}

onMounted(() => {
  window.addEventListener('nearby-alert:new', onNewAlertEvent);
});

onBeforeUnmount(() => {
  window.removeEventListener('nearby-alert:new', onNewAlertEvent);
});

async function onClaim(): Promise<void> {
  const alert = currentAlert.value;
  if (!alert || claiming.value) return;
  claiming.value = true;
  try {
    const result = await notificationStore.claimNearbyAlert(alert.id);
    if (result.free_cap_blocked) {
      errorMessage.value = t('nearby_alert_claim_error_free_cap');
      errorOpen.value = true;
      return;
    }
    if (!result.match_group_id) {
      errorMessage.value = t('nearby_alert_claim_error_already_claimed');
      errorOpen.value = true;
      return;
    }
    if (result.already_claimed) {
      errorMessage.value = t('nearby_alert_claim_error_already_claimed');
      errorOpen.value = true;
    }
    isOpen.value = false;
    await router.push(`/tabs/matches?highlight=${encodeURIComponent(result.match_group_id)}`)
      .catch((e) => console.error('[nearby banner navigate]', e));
  } catch (e: any) {
    if (e?.reason === 'free_cap_reached') {
      errorMessage.value = t('nearby_alert_claim_error_free_cap');
    } else {
      errorMessage.value = t('nearby_alert_claim_error_already_claimed');
    }
    errorOpen.value = true;
  } finally {
    claiming.value = false;
  }
}

async function onDismiss(): Promise<void> {
  const alert = currentAlert.value;
  isOpen.value = false;
  if (alert) {
    try {
      await notificationStore.markNearbyAlertAsRead(alert.id);
    } catch (e) {
      console.warn('[nearby banner dismiss]', e);
    }
  }
}

const buttons = computed(() => [
  {
    text: t('nearby_alert_claim_cta'),
    role: 'confirm',
    handler: () => { void onClaim(); return false; },
  },
  {
    text: '×',
    role: 'cancel',
    handler: () => { void onDismiss(); return false; },
  },
]);
</script>

<template>
  <ion-toast
    :is-open="isOpen"
    position="top"
    :duration="0"
    :header="t('nearby_alert_title')"
    :message="message"
    :buttons="buttons"
    color="primary"
    class="nearby-alert-toast"
    @did-dismiss="isOpen = false"
  />
  <ion-toast
    :is-open="errorOpen"
    position="top"
    :duration="3500"
    :message="errorMessage"
    color="warning"
    @did-dismiss="errorOpen = false"
  />
</template>

<style scoped>
.nearby-alert-toast::part(message) {
  font-size: 13px;
}
</style>
